import {cast, flow, getSnapshot, Instance, types} from 'mobx-state-tree';
import {ISelectOption, ISimpleTableConfigProps, ITreeActiveItem} from '@progress-fe/ui-kit';
import {ResetModel, RequestModel} from 'core/models';
import {DatasetColumnsMappings, DatasetInfo, ImportModeEnum, TechProcessApi} from 'api';

const ProjectDataset = types
  .compose(
    ResetModel,
    types.model('ProjectDataset', {
      projectUuid: '',
      checkpointUuid: '',

      datasetRelations: types.optional(types.array(types.frozen<ITreeActiveItem>()), []),
      datasetTable: types.maybeNull(
        types.frozen<ISimpleTableConfigProps<Record<string, string>>>()
      ),

      selectedRowKey: types.maybe(types.number),
      rowsOptions: types.optional(types.array(types.frozen<ISelectOption<number>>()), []),

      loadRequest: types.optional(RequestModel, {}),
      importRequest: types.optional(RequestModel, {}),
      resetRequest: types.optional(RequestModel, {}),
      rowKeyRequest: types.optional(RequestModel, {}),
      loadMappingRequest: types.optional(RequestModel, {}),
      changeMappingRequest: types.optional(RequestModel, {}),
      deleteMappingRequest: types.optional(RequestModel, {})
    })
  )
  .actions((self) => ({
    loadDataset: flow(function* () {
      const response: DatasetInfo = yield self.loadRequest.send(
        TechProcessApi.techProcessGetDataset.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid
        }
      );

      if (response?.data) {
        self.datasetTable = cast(response.data);
        self.selectedRowKey = response.selectedRowIndex ?? undefined;
        self.rowsOptions = cast(
          response.data?.rows.map((r) => {
            return {value: r.key, label: r.key.toString()};
          }) ?? []
        );
      } else {
        self.datasetTable = null;
        self.selectedRowKey = undefined;
        self.rowsOptions = cast([]);
      }
    }),

    importDataset: flow(function* (file: File, mode: ImportModeEnum) {
      yield self.importRequest.send(TechProcessApi.techProcessImportDataset.bind(TechProcessApi), {
        projectUuid: self.projectUuid,
        checkpointUuid: self.checkpointUuid,
        mode: mode,
        file: file
      });

      return self.importRequest.isDone;
    }),

    resetDataset: flow(function* (isOnlyClear: boolean) {
      yield self.resetRequest.send(TechProcessApi.techProcessClearDataset.bind(TechProcessApi), {
        projectUuid: self.projectUuid,
        checkpointUuid: self.checkpointUuid,
        onlyClear: isOnlyClear
      });

      return self.resetRequest.isDone;
    }),

    _selectRowKey: flow(function* (rowKey: number) {
      yield self.rowKeyRequest.send(
        TechProcessApi.techProcessSetDatasetRowSelector.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid,
          datasetRowKey: rowKey
        }
      );
    })
  }))
  .actions((self) => ({
    loadRelations: flow(function* () {
      const response: DatasetColumnsMappings = yield self.loadMappingRequest.send(
        TechProcessApi.techProcessGetMappingsOfDatasetColumns.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid
        }
      );

      if (self.loadMappingRequest.isDone && response) {
        self.datasetRelations = cast(
          response.mappings.map((m) => ({
            uuid: m.uuid,
            treeId: m.elementUuid,
            jsonPath: m.jsonPath,
            columnName: m.columnName
          }))
        );
      }
    })
  }))
  .actions((self) => ({
    createOrChangeRelation: flow(function* (jsonPath: string, columnName: string) {
      yield self.changeMappingRequest.send(
        TechProcessApi.techProcessMapDatasetColumn.bind(TechProcessApi),
        {
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid,
          datasetColumnMappingIn: {
            columnName: columnName,
            jsonPath: jsonPath
          }
        }
      );
      yield self.loadRelations();
    }),

    deleteRelation: flow(function* (uuid: string) {
      yield self.deleteMappingRequest.send(
        TechProcessApi.techProcessUnmapDatasetColumn.bind(TechProcessApi),
        {
          mappingUuid: uuid,
          projectUuid: self.projectUuid,
          checkpointUuid: self.checkpointUuid
        }
      );
      yield self.loadRelations();
    })
  }))
  .actions((self) => ({
    reload: flow(function* () {
      yield Promise.allSettled([self.loadDataset(), self.loadRelations()]);
    })
  }))
  .actions((self) => ({
    init: flow(function* (projectUuid: string, checkpointUuid: string) {
      self.projectUuid = projectUuid;
      self.checkpointUuid = checkpointUuid;
      yield self.reload();
    })
  }))
  .views((self) => ({
    get isLoading(): boolean {
      return self.loadRequest.isPending;
    },
    get isImporting(): boolean {
      return self.importRequest.isPending;
    },
    get isResetting(): boolean {
      return self.resetRequest.isPending;
    },
    get isRelationLoading(): boolean {
      return self.loadMappingRequest.isPending;
    },
    get isRelationChanging(): boolean {
      return self.changeMappingRequest.isPending || self.deleteMappingRequest.isPending;
    },
    get relationList(): Array<ITreeActiveItem> {
      return getSnapshot(self.datasetRelations);
    }
  }));

export type TProjectDatasetModel = Instance<typeof ProjectDataset>;

export {ProjectDataset};
